import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {useLocation} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useIsPlatform} from '@github-ui/use-is-platform'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {clsx} from 'clsx'
import {useCallback} from 'react'

import styles from './RepositoryProjects.module.css'

interface OpenClosedProjectsProps {
  openCount: number
  closedCount: number
}

export function OpenClosedProjects({openCount, closedCount}: OpenClosedProjectsProps) {
  const [searchParams] = useSearchParams()
  const stateParam = searchParams.get('state')
  const isMac = useIsPlatform(['mac'])
  const navigate = useNavigate()
  // Use React Router's reactive `useLocation()` instead of reading the
  // pathname off the module-level `ssrSafeWindow.location`. The React Compiler
  // sees `ssrSafeWindow` as a non-reactive module import and would freeze
  // `openHrefUrl` / `closedHrefUrl` (and the `useCallback` deriving them) at
  // the first render's pathname, producing stale tab links after navigation.
  const {pathname} = useLocation()

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, isOpen: boolean) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const isMetaKey = isMac ? event.metaKey : event.ctrlKey
      if (isMetaKey) {
        return
      }

      event.preventDefault()
      const params = new URLSearchParams(searchParams)
      if (isOpen) {
        params.delete('state')
      } else {
        params.set('state', 'closed')
      }
      const paramString = params.toString()
      const href = paramString ? `${pathname}?${paramString}` : pathname
      navigate(href)
    },
    [isMac, navigate, pathname, searchParams],
  )

  const openHrefUrl = pathname
  const closedHrefUrl = `${openHrefUrl}?state=closed`

  const {isBrowser} = useRuntimeEnvironment()

  return (
    <div>
      <ul className={`list-style-none ${clsx(styles.tabsContainer)}`}>
        <li key="section-filter-0">
          <ListViewSectionFilterLink
            key="open"
            title="Open"
            isSelected={isBrowser && stateParam !== 'closed'}
            count={openCount.toLocaleString()}
            href={openHrefUrl}
            onClick={e => onClick(e, true)}
            data-testid="open-projects-tab"
          />
        </li>
        <li key="section-filter-1">
          <ListViewSectionFilterLink
            key="closed"
            title="Closed"
            isSelected={isBrowser && stateParam === 'closed'}
            count={closedCount.toLocaleString()}
            href={closedHrefUrl}
            onClick={e => onClick(e, false)}
            data-testid="closed-projects-tab"
          />
        </li>
      </ul>
    </div>
  )
}
